"use client";

import type { Variants } from "framer-motion";
import { motion } from "framer-motion";
import moment from "moment";
import React, { useState } from "react";

import BookingDoneInfo from "@/components/sections/Booking/BookingDoneInfo";
import { useTranslation } from "@/hooks/useTranslation";
import submitBookingForm from "@/lib/submitBookingForm";
import type { Apartment, Room } from "@/payload-types";
import type { BookingData } from "@/types";
import type { PayLoadData } from "@/utils/payloadUtil";

import BookingInfo from "./BookingInfo";
import CustomerInfo from "./CustomerInfo";

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

const initBookingForm: BookingData = {
  travelDates: {
    arrival: moment().format("YYYY-MM-DD"),
    departure: moment().add(1, "d").format("YYYY-MM-DD"),
  },
  people: {
    adults: 0,
    children: 0,
  },
  accommodation: {
    roomApartment: "",
  },
  customer: {
    firstName: "",
    surname: "",
    email: "",
    phoneNumber: "",
    country: "",
  },
  status: "pending",
  specialRequest: "",
};

type Props = {
  rooms: PayLoadData<Room[]> | undefined;
  apartments: PayLoadData<Apartment[]> | undefined;
};

export default function BookingForm({ rooms, apartments }: Props) {
  const [formData, setFormData] = useState<BookingData>(initBookingForm);
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const onInputChange = (newFormData: BookingData) => {
    setFormData(newFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");
    try {
      if (!formData.accommodation.roomApartment) {
        setSubmitError(
          t({
            en: "Please choose your room or apartment.",
            de: "Bitte wählen Sie Ihr Zimmer oder Ihre Wohnung.",
          })
        );
        setIsSubmitting(false);
        return;
      }
      // Use the new submitContactForm function
      const { success, messages } = await submitBookingForm(formData);

      if (success) {
        setSubmitSuccess(true);
      } else if (messages) setSubmitError(t(messages));
    } catch (error) {
      console.error("Form submission error details:", error);
      setSubmitError(
        t({
          en: "An error occurred, please try again later.",
          de: "Ein Fehler ist aufgetreten, bitte versuchen Sie es später erneut.",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="m-auto max-w-6xl">
      <section id="contact" className="pt-0">
        <motion.div initial="hidden" animate="visible" variants={itemVariants}>
          {submitSuccess ? (
            <BookingDoneInfo
              data={formData}
              backToFormAction={() => {
                setSubmitSuccess(false);
                setFormData(initBookingForm);
              }}
            />
          ) : (
            <form
              onSubmit={handleSubmit}
              className="container mx-auto gap-6 overflow-hidden bg-white p-8 shadow-md lg:gap-0"
            >
              <h2 className="mb-6 border-b border-gray-100 pb-3 text-xl font-bold text-primary-600">
                {t({ de: "Buchungsinformationen", en: "Booking Information" })}
              </h2>
              {submitError && (
                <div className="mb-4 bg-red-100 p-3 text-red-700">
                  {submitError}
                </div>
              )}
              <div className="mb-10 grid grid-cols-1 lg:grid-cols-2">
                <BookingInfo
                  formData={formData}
                  onInputChange={onInputChange}
                  apartments={apartments}
                  rooms={rooms}
                />
                <CustomerInfo
                  formData={formData}
                  onInputChange={onInputChange}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`mx-auto flex w-fit items-center rounded-full bg-primary-600 px-8 py-4 text-lg font-bold text-white shadow-lg transition-colors duration-500 hover:bg-primary-500`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="-ml-1 mr-3 size-5 animate-spin text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t({ de: "Senden...", en: "Sending..." })}
                  </>
                ) : (
                  t({ de: "Jetzt Anfragen", en: "Book now" })
                )}
              </button>
            </form>
          )}
        </motion.div>
      </section>
    </div>
  );
}
